import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Home,
  CalendarRange,
  CalendarClock,
  History,
  User,
  LogOut,
  Users,
  Sparkles,
  Truck,
  MessageSquare,
} from 'lucide-react';
import { useAuth } from '@shared/authContext';
import { useUnreadMessageCount } from '@shared/messaging/useMessaging';
import { useMessageStream } from '@shared/messaging/useMessageStream';
import PwaUpdatePrompt from './PwaUpdatePrompt';
import AssistantPanel from './assistant/AssistantPanel';

const navigation = [
  { name: 'Today', href: '/today', icon: CalendarClock },
  { name: 'Messages', href: '/messages', icon: MessageSquare, technicianOnly: true },
  { name: 'Calendar', href: '/calendar', icon: CalendarRange },
  { name: 'Dashboard', href: '/dashboard', icon: Home },
  { name: 'Calls', href: '/my-service-calls', icon: History },
  { name: 'Truck Stock', href: '/my-truck-stock', icon: Truck, technicianOnly: true },
  { name: 'Clients', href: '/clients', icon: Users },
  { name: 'Profile', href: '/profile', icon: User },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [assistantOpen, setAssistantOpen] = useState(false);

  const { data: unreadResponse } = useUnreadMessageCount({
    enabled: user?.role === 'technician',
    refetchInterval: user?.role === 'technician' ? 60000 : false,
  });
  const unreadCount = unreadResponse?.data?.unreadCount ?? 0;

  useMessageStream({
    enabled: user?.role === 'technician',
  });

  const visibleNavigation = navigation.filter(
    (item) => !item.technicianOnly || user?.role === 'technician'
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="flex min-h-screen flex-col">
        <header className="sticky top-0 z-20 border-b border-slate-200 bg-slate-50/95 backdrop-blur">
          <div className="mx-auto flex w-full max-w-6xl items-center justify-between px-4 py-3 sm:px-6">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary-600 text-white shadow-sm overflow-hidden">
                <img
                  src={`${import.meta.env.BASE_URL}pwa-192.png`}
                  alt="K&R Services"
                  className="h-10 w-10"
                />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">K&amp;R Tech</p>
                <p className="text-base font-semibold text-slate-900">Technician App</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="hidden items-center gap-2 rounded-full bg-slate-100 px-3 py-2 text-sm font-medium text-slate-600 sm:flex">
                <User className="h-4 w-4" />
                <span>{user?.firstName} {user?.lastName}</span>
              </div>
              <button onClick={logout} className="btn btn-secondary btn-sm">
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </button>
            </div>
          </div>
        </header>

        <PwaUpdatePrompt />

        <main className="flex-1 pb-24">
          <div className="mx-auto w-full max-w-6xl px-4 py-6 sm:px-6 lg:px-8">
            {children}
          </div>
        </main>

        <button
          type="button"
          onClick={() => setAssistantOpen(true)}
          aria-label="Open Tech Assistant"
          className="fixed bottom-24 right-4 z-30 flex items-center justify-center rounded-full bg-primary-600 p-3.5 text-white shadow-lg transition-colors hover:bg-primary-700 active:scale-95"
        >
          <Sparkles className="h-6 w-6" />
        </button>

        <AssistantPanel open={assistantOpen} onClose={() => setAssistantOpen(false)} />

        <nav className="tabbar fixed bottom-0 left-0 right-0 z-30 bg-slate-50/95">
          <div className="mx-auto flex w-full max-w-6xl items-center justify-around px-2 py-2 sm:py-3">
            {visibleNavigation.map((item) => {
              const isActive = location.pathname === item.href;
              const showBadge = item.href === '/messages' && unreadCount > 0;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`tabbar-item relative ${isActive ? 'tabbar-item-active' : ''}`}
                >
                  <span className="relative inline-flex">
                    <item.icon className={`tabbar-icon ${isActive ? 'text-primary-600' : ''}`} />
                    {showBadge && (
                      <span className="absolute -top-1.5 -right-2 min-w-[1rem] h-4 px-1 rounded-full bg-primary-600 text-white text-[10px] font-semibold leading-none flex items-center justify-center">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </span>
                  <span className="tabbar-label">{item.name}</span>
                </Link>
              );
            })}
          </div>
        </nav>
      </div>
    </div>
  );
}
